import React from "react";
import {
  BarChart3,
  ShoppingCart,
  Users,
  Settings,
  Clock,
  Shield,
} from "lucide-react";
import { useLanguage } from "../../context/LanguageProvider";

interface Feature {
  icon: React.ReactNode;
  titleKey: string;
  descriptionKey: string;
  color: string;
}

const features: Feature[] = [
  {
    icon: <ShoppingCart className="h-6 w-6" />,
    titleKey: "features_inventory_title",
    descriptionKey: "features_inventory_description",
    color: "from-blue-500 to-cyan-400",
  },
  {
    icon: <BarChart3 className="h-6 w-6" />,
    titleKey: "features_analytics_title",
    descriptionKey: "features_analytics_description",
    color: "from-primary to-violet-500",
  },
  {
    icon: <Users className="h-6 w-6" />,
    titleKey: "features_customer_title",
    descriptionKey: "features_customer_description",
    color: "from-green-500 to-emerald-400",
  },
  {
    icon: <Settings className="h-6 w-6" />,
    titleKey: "features_integration_title",
    descriptionKey: "features_integration_description",
    color: "from-orange-500 to-amber-400",
  },
  {
    icon: <Clock className="h-6 w-6" />,
    titleKey: "features_updates_title",
    descriptionKey: "features_updates_description",
    color: "from-red-500 to-rose-400",
  },
  {
    icon: <Shield className="h-6 w-6" />,
    titleKey: "features_security_title",
    descriptionKey: "features_security_description",
    color: "from-purple-500 to-pink-500",
  },
];

const FeaturesSection: React.FC = () => {
  const { t } = useLanguage();

  return (
    <section className="py-24 bg-white relative" id="features">
      <div className="absolute inset-0 opacity-5 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-primary blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-secondary blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            <span className="flex h-2 w-2 rounded-full bg-primary mr-2"></span>
            {t("features_badge")}
          </div>

          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            {t("features_title_part1")}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
              {t("features_title_part2")}
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t("features_description")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 group"
            >
              <div
                className="mb-4 inline-flex p-3 rounded-lg bg-gradient-to-br text-black shadow-md group-hover:scale-110 transition-transform duration-300 ease-in-out"
                style={
                  {
                    backgroundImage: `linear-gradient(to bottom right, var(--tw-gradient-stops))`,
                    "--tw-gradient-from": feature.color
                      .split(" ")[0]
                      .replace("from-", ""),
                    "--tw-gradient-to": feature.color
                      .split(" ")[1]
                      .replace("to-", ""),
                  } as any
                }
              >
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors duration-300">
                {t(feature.titleKey)}
              </h3>
              <p className="text-gray-600">{t(feature.descriptionKey)}</p>{" "}
            </div>
          ))}
        </div>

        <div className="mt-20 text-center">
          <a
            href="#pricing"
            className="inline-flex items-center px-6 py-3 bg-gray-100 text-primary font-medium rounded-lg hover:bg-gray-200 transition-colors duration-300"
          >
            {t("features_button_seePricing")}
            <svg
              className="ml-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
